import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionScope, 
  ConstructionScopeItem, 
  ScopeStats, 
  ScopeFilters 
} from '../types/construction-scope.types';

export const ConstructionScopesService = {
  /**
   * Danh sách phạm vi thi công
   */
  async getScopes(companyId: string, filters: ScopeFilters = {}): Promise<{ data: ConstructionScope[], total: number }> {
    let query = supabase
      .from('construction_scopes')
      .select(`
        *,
        project:projects(id, name),
        contract:construction_contracts(id, contract_code, contract_name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.contractId) query = query.eq('contract_id', filters.contractId);
    if (filters.search) {
      query = query.or(`scope_name.ilike.%${filters.search}%,scope_code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionScope[],
      total: count || 0
    };
  },

  /**
   * Chi tiết Scope
   */
  async getScopeDetail(id: string): Promise<ConstructionScope> {
    const { data, error } = await supabase
      .from('construction_scopes')
      .select(`
        *,
        project:projects(id, name),
        contract:construction_contracts(id, contract_code, contract_name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionScope;
  },

  /**
   * Danh sách hạng mục trong Scope
   */
  async getScopeItems(scopeId: string): Promise<ConstructionScopeItem[]> {
    const { data, error } = await supabase
      .from('construction_scope_items')
      .select(`
        *,
        zone:project_zones(id, name),
        phase:project_phases(id, name)
      `)
      .eq('scope_id', scopeId)
      .order('work_category', { ascending: true });

    if (error) throw error;
    return data as ConstructionScopeItem[];
  },

  /**
   * Thống kê Scope
   */
  async getScopeStats(companyId: string): Promise<ScopeStats> {
    const { data: scopes } = await supabase.from('construction_scopes').select('status_code').eq('company_id', companyId);
    const { data: items } = await supabase.from('construction_scope_items').select('id').eq('company_id', companyId);

    const totalScopes = scopes?.length || 0;
    const approved = scopes?.filter(s => s.status_code === 'approved' || s.status_code === 'locked').length || 0;
    const totalItems = items?.length || 0;

    return {
      totalScopes,
      approvedScopes: approved,
      totalItems,
      avgItemsPerScope: totalScopes > 0 ? totalItems / totalScopes : 0
    };
  },

  /**
   * CRUD
   */
  async createScope(data: Partial<ConstructionScope>): Promise<ConstructionScope> {
    const { data: result, error } = await supabase
      .from('construction_scopes')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionScope;
  },

  async updateScope(id: string, updates: Partial<ConstructionScope>): Promise<void> {
    const { error } = await supabase
      .from('construction_scopes')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};
